import { Component, computed, Input, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { IconComponent } from '../../shared/icon/icon.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { ShipmentReferenceEditorComponent } from '../shipment-reference-editor/shipment-reference-editor.component';
import { ReferencesModification } from '../../../models/references-modification';
import { Store } from '@ngrx/store';
import { AppState } from '../../../store';
import {
    selectShipmentEditorReferenceGroups,
    selectShipmentEditorReferences,
    selectShipmentEditorShipment
} from '../../../store/shipment-editor/shipment-editor.state';
import { delay, map, tap, withLatestFrom } from 'rxjs/operators';
import { selectCourierOptions, selectReferenceDefinitions, selectReferenceGroups } from '../../../store/profile/profile.state';
import {
    shipmentEditorAddReference, shipmentEditorPatchReferences,
    shipmentEditorRemoveReference,
    shipmentEditorUpdateOptionsSection
} from '../../../store/shipment-editor/shipment-editor.actions';
import { Shipment } from '../../../models/shipment';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { combineLatest, Subscription } from 'rxjs';
import { DateService } from '../../shared/services/date.service';
import { emptyShipment } from '../../../constants/empty-shipment';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow, MatHeaderRowDef,
    MatRow, MatRowDef,
    MatTable
} from '@angular/material/table';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatInput } from '@angular/material/input';
import { toSignal } from '@angular/core/rxjs-interop';

type ReferencesMap = Record<string, string>;
type ReferenceRow = Record<string, string>;

@Component({
    selector: 'app-shipment-references',
    imports: [
        ReactiveFormsModule,
        MatCell,
        MatCellDef,
        MatCheckbox,
        MatColumnDef,
        MatHeaderCell,
        MatInput,
        MatHeaderCellDef,
        MatTable,
        MatHeaderRow,
        MatRow,
        MatHeaderRowDef,
        MatRowDef,
        MatButton
    ],
    templateUrl: './shipment-references.component.html',
    styleUrl: './shipment-references.component.scss'
})
export class ShipmentReferencesComponent {

    selectedIds = new Set<number>();

    constructor(private store: Store<AppState>) {}

    // Shape: { "ref1": "value1", "user1": "username1", "ref2": "value2", ... }


// Observable -> signal (assumes you already have the selector)
    public references = toSignal<ReferencesMap>(
        this.store.select(selectShipmentEditorReferences)
    );
    public referenceGroups = toSignal(this.store.select(selectReferenceGroups));
    public referenceDefinitions = toSignal(this.store.select(selectReferenceDefinitions));

    public referenceColumns = computed<string[]>(
        () => [ '_select', ...this.referenceGroups().map(g => g.name) ]
    );

    // Computed rows: [ { ref: 'value1', user: 'username1' }, { ref: 'value2', user: 'username2' }, ... ]
    public referenceRows = computed<ReferenceRow[]>(() => {
        const refs = this.references() ?? {};
        const entries = Object.entries(refs);

        if (entries.length === 0) { return []; }

        // Helpers to extract row number (1-based) and column name
        const getRowIndex = (k: string): number | null => {
            const m = k.match(/(\d+)$/);         // trailing digits
            return m ? parseInt(m[1], 10) : null;
        };
        const getColName = (k: string): string =>
            k.replace(/(\d+)$/, '');             // strip trailing digits

        // Find the largest row index so we can pre-size with empty objects
        let maxRow = 0;
        for (const [k] of entries) {
            const n = getRowIndex(k);
            if (n && n > maxRow) { maxRow = n; }
        }
        if (maxRow === 0) { return []; }

        // Pre-fill rows (1..maxRow) as empty objects; array is 0-based
        const rows: ReferenceRow[] = Array.from({ length: maxRow }, () => ({}));

        // Populate rows
        for (const [k, v] of entries) {
            const n = getRowIndex(k);
            if (!n) { continue; }                    // skip keys without a trailing number
            const col = getColName(k);
            rows[n - 1][col] = v ?? '';
        }
        return rows;
    });

    trackBy(index: number, item: any): number {
        return index;
    }

    toggleSelection(): void {
        if (this.selectedIds.size === 0) {
            for (let i = 0; i < this.referenceRows().length; i++) {
                this.selectedIds.add(i);
            }
        } else {
            this.selectedIds.clear();
        }
    }

    isAllSelected(): boolean {
        return this.selectedIds.size === this.referenceRows().length && this.selectedIds.size > 0;
    }

    isSelectionIndeterminate(): boolean {
        return this.selectedIds.size > 0 && this.selectedIds.size < this.referenceRows().length;
    }

    toggleSelectRow(i: number): void {
        if (this.selectedIds.has(i)) {
            this.selectedIds.delete(i);
        } else {
            this.selectedIds.add(i);
        }
    }

    isSelected(i: number): boolean {
        return this.selectedIds.has(i);
    }

    onFieldBlur(index: number, name: string, $event: FocusEvent): void {
        const input = $event.target as HTMLInputElement;
        const previousValue = this.references()[ name + index ] || '';
        if (previousValue === input.value) { return; }
        const references: string[] = [];
        references[ name + index ] = input.value;
        this.store.dispatch(shipmentEditorPatchReferences({ references }));
    }

    addReference(): void {
        this.store.dispatch(shipmentEditorAddReference());
    }

    deleteReferences(): void {
        Array.from(this.selectedIds).reverse().forEach(index => this.store.dispatch(shipmentEditorRemoveReference({ index })));
        this.selectedIds.clear();
    }

    disableAdd(): boolean {
        return this.referenceDefinitions()?.max_count !== undefined && this.referenceRows().length >= this.referenceDefinitions().max_count;
    }

    disableDelete(): boolean {
        if (this.selectedIds.size === 0) { return true; }
        const newCount = this.referenceRows().length - this.selectedIds.size;
        return newCount < this.referenceDefinitions().min_count;
    }
}
